import React, { useMemo } from 'react';
import { Form, Formik, FormikHelpers, FormikProps, FormikState } from 'formik';
import { IconColor, SalvButton, SalvLabel, SalvModal, Size } from '@salvapp/ui-components';
import * as Yup from 'yup';

import { InternalApiRiskRule, InternalApiScenarioStatus, InternalApiScenarioVersionStatus } from '../../api-internal';
import { getScenarioVersionStatus, scenarioVersionStatusColorVariant } from '../../common/versioning/helper';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useMe } from '../../organisation/useMe';
import { CAN_MODIFY_RISK_RULE } from '../../auth/permissions';
import { useAddRiskRuleVersion, useUpdateRiskRuleVersion } from './riskRuleQueryActions';
import RiskRuleVersionLeftPanel from './RiskRuleVersionLeftPanel';
import RiskRuleVersionRightPanel from './RiskRuleVersionRightPanel';
import './RiskRuleVersionModal.scss';

export interface RiskRuleVersionModalFormValues {
  status: InternalApiScenarioVersionStatus;
  details: string;
  query: string;
  note: string;
  weight: number;
}

export interface RiskRuleVersionModalProps {
  riskRule: InternalApiRiskRule;
  versionId: number | null;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  disabled?: boolean;
}

export const RiskRuleVersionModal = ({
  riskRule,
  versionId,
  showModal,
  setShowModal,
  disabled = false,
}: RiskRuleVersionModalProps) => {
  const { permissions } = useMe();
  const dispatch = useDispatch();

  const version = riskRule.versions.find((v) => v.versionId === versionId) || null;

  const { mutate: updateRiskRuleVersion } = useUpdateRiskRuleVersion();
  const { mutate: addRiskRuleVersion } = useAddRiskRuleVersion();

  const isDraft = !version || version.status === InternalApiScenarioVersionStatus.DRAFT;

  const canEditVersion = isDraft && permissions.includes(CAN_MODIFY_RISK_RULE) && !disabled;

  const initialValues: RiskRuleVersionModalFormValues = useMemo(
    () => ({
      details: version?.details || riskRule.versions[0]?.details || '',
      query: version?.content || riskRule.versions[0]?.content || '',
      note: version?.note || riskRule.versions[0]?.note || '',
      status: version?.status || InternalApiScenarioVersionStatus.DRAFT,
      weight: version?.weight || riskRule.versions[0]?.weight || 0,
    }),
    [version]
  );

  const initialFormikState: Partial<FormikState<RiskRuleVersionModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const validationSchema = Yup.object().shape(
    {
      details: Yup.string().required('Details are required'),
      note: Yup.string().required('Note is required'),
      query: Yup.string().required('Query is required'),
    },
    []
  );

  const submitVersion = async (
    formValues: RiskRuleVersionModalFormValues,
    { setSubmitting }: FormikHelpers<RiskRuleVersionModalFormValues>
  ) => {
    if (version) {
      updateRiskRuleVersion({
        handle: riskRule.id,
        id: version.versionId,
        update: {
          status: formValues.status,
          note: formValues.note,
          content: formValues.query,
          details: formValues.details,
          weight: formValues.weight,
        },
      });
    } else {
      addRiskRuleVersion({
        handle: riskRule.id,
        update: {
          status: formValues.status,
          note: formValues.note,
          content: formValues.query,
          details: formValues.details,
          weight: formValues.weight,
        },
      });
    }

    setSubmitting(false);
    setShowModal(false);
  };

  const getTitle = () => {
    if (!version) {
      return 'Add risk rule version';
    }

    if (canEditVersion) {
      return `Edit risk rule version ${version.versionId}`;
    } else {
      return `View risk rule version ${version.versionId}`;
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitVersion}
      validationSchema={validationSchema}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {({ resetForm, setFieldValue, handleSubmit, isSubmitting }: FormikProps<RiskRuleVersionModalFormValues>) => {
        const handleClose = () => {
          resetForm(initialFormikState);
          setShowModal(false);
        };

        const submitCurrentVersion = (e: any) => {
          setFieldValue('status', InternalApiScenarioVersionStatus.LIVE).then(() => handleSubmit(e));
        };

        const isNewVersion = !versionId;
        return (
          <SalvModal
            show={showModal && (isNewVersion || !!version)}
            onHide={handleClose}
            className='scenario-version-modal'
            allowDismissal={false}
          >
            <Form>
              <SalvModal.Header>{getTitle()}</SalvModal.Header>
              <SalvModal.Body>
                <div className='scenario-version-modal-container'>
                  <div className='left-panel'>
                    {version && (
                      <div className='d-flex p-b-24'>
                        <div className='text-strong-s m-r-8'>Version status</div>
                        <SalvLabel variant={scenarioVersionStatusColorVariant(version.status)}>
                          {getScenarioVersionStatus(version.status)}
                        </SalvLabel>
                      </div>
                    )}

                    <RiskRuleVersionLeftPanel isDisabled={isSubmitting || !canEditVersion} />
                  </div>

                  <RiskRuleVersionRightPanel handle={riskRule.id} isDisabled={isSubmitting || !canEditVersion} />
                </div>
              </SalvModal.Body>
              {canEditVersion && (
                <SalvModal.Footer>
                  <SalvButton variant='muted' label='Save as draft' size={Size.LARGE} type='submit' />

                  <SalvButton
                    variant='primary'
                    label='Make version current'
                    size={Size.LARGE}
                    onClick={(e) =>
                      dispatch(
                        showConfirmationModal({
                          title: 'Are you sure you want to make this version current?',
                          iconColor: IconColor.DANGER,
                          description:
                            riskRule.status === InternalApiScenarioStatus.ACTIVE
                              ? 'The new settings will be applied for risk rule execution after this'
                              : 'This risk rule will start generating risks, which might have serious impact on your compliance operations.',
                          confirmationText: 'Yes',
                          cancelText: 'No',
                          onConfirm: () => submitCurrentVersion(e),
                        })
                      )
                    }
                  />
                </SalvModal.Footer>
              )}
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default RiskRuleVersionModal;
